import { Component, inject } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { AnalyticsService } from '../../../services/analytics.service';
import { RelatorioOrcamento } from '../../../models/financeiro.model';
import { formatarMoedaBRL } from '../../../core/utils/moeda.util';

@Component({
  selector: 'app-relatorio-orcamentos',
  standalone: true,
  imports: [CommonModule, FormsModule],
  providers: [DatePipe],
  templateUrl: './relatorio-orcamentos.component.html',
  styleUrl: './relatorio-orcamentos.component.css'
})
export class RelatorioOrcamentosComponent {
  private analyticsService = inject(AnalyticsService);
  private toastr = inject(ToastrService);
  private datePipe = inject(DatePipe);

  statusOrcamento = 'CONCLUIDO';
  dataInicial = '';
  dataFinal = '';

  orcamentos: RelatorioOrcamento[] = [];
  carregado = false;
  carregando = false;
  tentouBuscar = false;
  dataGeracao = '';

  get dataInicialInvalida(): boolean {
    return this.tentouBuscar && this.dataInicial.trim() === '';
  }

  get dataFinalInvalida(): boolean {
    return this.tentouBuscar && this.dataFinal.trim() === '';
  }

  get totalValor(): number {
    return this.orcamentos.reduce((acc, o) => acc + (o.valorTotal ?? 0), 0);
  }

  get totalSubtotal(): number {
    return this.orcamentos.reduce((acc, o) => acc + (o.subtotal ?? 0), 0);
  }

  buscar(): void {
    this.tentouBuscar = true;

    if (this.dataInicial.trim() === '' || this.dataFinal.trim() === '') {
      this.toastr.warning('Informe a data inicial e a data final para gerar o relatório.', 'Período obrigatório');
      return;
    }

    this.carregando = true;
    this.carregado = false;

    const status = this.statusOrcamento.trim() !== '' ? this.statusOrcamento : undefined;

    this.analyticsService.getRelatorioOrcamentos(status, this.dataInicial, this.dataFinal).subscribe({
      next: (data) => {
        this.orcamentos = data;
        this.dataGeracao = this.datePipe.transform(new Date(), 'dd/MM/yyyy HH:mm') ?? '';
        this.carregado = true;
        this.carregando = false;
      },
      error: () => {
        this.toastr.error('Erro ao carregar relatório de orçamentos.', 'Erro');
        this.carregando = false;
      }
    });
  }

  imprimir(): void {
    const esc = (value: string | number | null | undefined): string =>
      String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');

    const brl = (value: number) => (value != null ? formatarMoedaBRL(value) : 'R$ 0,00');

    const periodLabel = `${this.formatDate(this.dataInicial)} a ${this.formatDate(this.dataFinal)}`;

    const statusLabel = this.statusOrcamento || 'Todos';

    const rows = this.orcamentos.map((orc, i) => `
      <tr class="${i % 2 === 0 ? 'even' : 'odd'}">
        <td>${esc(orc.id)}</td>
        <td>${esc(orc.nomeCliente)}</td>
        <td>${esc(orc.nomeVendedor)}</td>
        <td class="center"><span class="status status-${esc((orc.statusOrcamento ?? '').toLowerCase())}">${esc(orc.statusOrcamento)}</span></td>
        <td class="center">${esc(this.formatDate(orc.dataOrcamento))}</td>
        <td class="center">${esc(this.formatDate(orc.validadeOrcamento))}</td>
        <td class="right">${brl(orc.subtotal)}</td>
        <td class="right">${(orc.descontoAplicado ?? 0).toFixed(2)}%</td>
        <td class="right">${brl(orc.custoInstalacao)}</td>
        <td class="right bold">${brl(orc.valorTotal)}</td>
      </tr>`).join('');

    const html = `<!DOCTYPE html>
<html lang="pt-BR">
<head>
  <meta charset="UTF-8"/>
  <title>Relatório de Orçamentos</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: Arial, Helvetica, sans-serif; font-size: 10pt; color: #000; background: #fff; }

    @page {
      size: A4 landscape;
      margin: 14mm 12mm 18mm 12mm;
      @bottom-left   { content: "Relatório de Orçamentos"; font-size: 7.5pt; color: #555; }
      @bottom-center { content: "Página " counter(page) " de " counter(pages); font-size: 7.5pt; color: #000; }
      @bottom-right  { content: "Gerado em: ${esc(this.dataGeracao)}"; font-size: 7.5pt; color: #555; }
    }

    /* Cabeçalho */
    .header { text-align: center; margin-bottom: 10px; }
    .header h1 { font-size: 16pt; font-weight: 700; letter-spacing: 0.5px; }
    .header .meta { font-size: 9pt; color: #444; margin-top: 4px; }
    .header .divider { border: none; border-top: 2px solid #000; margin-top: 8px; }

    /* Tabela */
    table { width: 100%; border-collapse: collapse; margin-top: 4px; }
    thead tr { background-color: #1a1a2e; color: #fff; print-color-adjust: exact; -webkit-print-color-adjust: exact; }
    thead th { padding: 6px 7px; font-size: 9pt; font-weight: 700; text-align: left; border: 1px solid #333; }
    tbody td { padding: 4px 7px; font-size: 9pt; border: 1px solid #ccc; vertical-align: middle; }
    tbody tr.even { background-color: #f5f5f5; print-color-adjust: exact; -webkit-print-color-adjust: exact; }
    tbody tr { page-break-inside: avoid; }
    thead { display: table-header-group; }
    tfoot { display: table-footer-group; }

    /* Totais */
    tfoot tr td { padding: 5px 7px; font-size: 9pt; font-weight: 700; border-top: 2px solid #000; border-bottom: 1px solid #ccc; background-color: #e0e0e0; print-color-adjust: exact; -webkit-print-color-adjust: exact; }

    /* Alinhamentos */
    .center { text-align: center; }
    .right  { text-align: right; }
    .bold   { font-weight: 700; }
    thead th.right  { text-align: right; }
    thead th.center { text-align: center; }

    /* Status */
    .status { display: inline-block; padding: 1px 6px; border-radius: 3px; font-size: 8pt; font-weight: 600;
               border: 1px solid #555; }
    .status-concluido { background-color: #d1e7dd; color: #0a3622; print-color-adjust: exact; -webkit-print-color-adjust: exact; }
    .status-aberto    { background-color: #fff3cd; color: #4a3800; print-color-adjust: exact; -webkit-print-color-adjust: exact; }
    .status-cancelado { background-color: #e2e3e5; color: #383d41; print-color-adjust: exact; -webkit-print-color-adjust: exact; }

    /* Rodapé de resumo */
    .summary { margin-top: 14px; font-size: 9pt; color: #333; }
  </style>
</head>
<body>
  <div class="header">
    <h1>Relatório de Orçamentos</h1>
    <div class="meta">
      Período: ${esc(periodLabel)} &nbsp;|&nbsp; Status: ${esc(statusLabel)} &nbsp;|&nbsp; Total: ${esc(this.orcamentos.length)} registro(s) &nbsp;|&nbsp; Gerado em: ${esc(this.dataGeracao)}
    </div>
    <hr class="divider"/>
  </div>

  <table>
    <thead>
      <tr>
        <th>ID</th>
        <th>Cliente</th>
        <th>Vendedor</th>
        <th class="center">Status</th>
        <th class="center">Data Orçamento</th>
        <th class="center">Validade</th>
        <th class="right">Subtotal</th>
        <th class="right">Desconto</th>
        <th class="right">Custo Instal.</th>
        <th class="right">Valor Total</th>
      </tr>
    </thead>
    <tbody>${rows}</tbody>
    <tfoot>
      <tr>
        <td colspan="6" class="right">Totais:</td>
        <td class="right">${brl(this.totalSubtotal)}</td>
        <td></td>
        <td></td>
        <td class="right">${brl(this.totalValor)}</td>
      </tr>
    </tfoot>
  </table>

  <div class="summary">
    Subtotal geral: <strong>${brl(this.totalSubtotal)}</strong> &nbsp;&nbsp; Valor total geral: <strong>${brl(this.totalValor)}</strong>
  </div>
</body>
</html>`;

    const win = window.open('', '_blank', 'width=1100,height=750');
    if (!win) {
      this.toastr.warning('O navegador bloqueou a abertura da janela de impressão. Permita pop-ups e tente novamente.');
      return;
    }
    win.document.open();
    win.document.write(html);
    win.document.close();
    win.focus();
    setTimeout(() => { win.print(); win.close(); }, 500);
  }

  private formatDate(value: string | undefined): string {
    if (!value) return '';
    return this.datePipe.transform(value, 'dd/MM/yyyy') ?? value;
  }

  limpar(): void {
    this.statusOrcamento = 'CONCLUIDO';
    this.dataInicial = '';
    this.dataFinal = '';
    this.orcamentos = [];
    this.carregado = false;
    this.tentouBuscar = false;
  }
}
